import { doc, getDoc, setDoc, writeBatch, collection, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import imageCompression from 'browser-image-compression';

const SETTINGS_DOC_REF = doc(db, 'parameters', 'system-settings');

/**
 * Obtiene la configuración maestra del sistema (Branding, Colores, Keys).
 */
export const getSystemSettings = async () => {
    try {
        const docSnap = await getDoc(SETTINGS_DOC_REF);
        if (docSnap.exists()) {
            return docSnap.data();
        }
        return null;
    } catch (error) {
        console.error("Error fetching system settings:", error);
        throw error;
    }
};

/**
 * Guarda o actualiza la configuración maestra.
 * Usa merge: true para no sobrescribir campos no enviados.
 */
export const saveSystemSettings = async (settingsData) => {
    try {
        await setDoc(SETTINGS_DOC_REF, settingsData, { merge: true });
    } catch (error) {
        console.error("Error saving system settings:", error);
        throw error;
    }
};

/**
 * Sube imágenes del sistema (Logos, Marcas de agua).
 * Incluye compresión automática antes de subir.
 */
export const uploadSystemImage = async (file, pathPrefix = 'system') => {
    if (!file) return null;
    try {
        const compressedFile = await imageCompression(file, { 
            maxSizeMB: 0.5, 
            maxWidthOrHeight: 800 
        });
        
        const storageRef = ref(storage, `${pathPrefix}/${Date.now()}_${file.name}`);
        await uploadBytes(storageRef, compressedFile);
        return await getDownloadURL(storageRef);
    } catch (error) {
        console.error("Error uploading system image:", error);
        throw error;
    }
};

/**
 * Inicializa los contadores (folios) del sistema si no existen.
 * Es una operación segura: solo crea si el documento no existe.
 */
export const initializeSystemCounters = async () => {
    try {
        const batch = writeBatch(db);
        
        // Definición de contadores base del sistema
        const counters = [
            { path: 'counters/purchaseOrders', data: { current: 0 } },
            { path: 'counters/quoteRequests', data: { current: 0 } },
            { path: 'expenseCounter/counter', data: { current: 0 } },
            { path: 'quoteCounter/counter', data: { current: 1000 } }, // Cotizaciones inician en 1000
            { path: 'accountingCounter/counter', data: { current: 0 } },
            { path: 'counters/reports', data: { current: 0 } }
        ];

        for (const counter of counters) {
            const [collectionName, docId] = counter.path.split('/');
            const docRef = doc(db, collectionName, docId);
            const docSnap = await getDoc(docRef);
            
            if (!docSnap.exists()) {
                batch.set(docRef, counter.data);
            }
        }

        await batch.commit();
        return true;
    } catch (error) {
        console.error("Error initializing counters:", error);
        throw error;
    }
};

/**
 * Genera un objeto JSON masivo con toda la data crítica del negocio.
 * Útil para respaldos manuales.
 */
export const createFullBackup = async () => {
    const collectionsToBackup = [
        'users',
        'clients',
        'products',
        'quotes',
        'providers',
        'accounting', // Ingresos/Egresos
        'reports',    // Informes Técnicos
        'parameters'  // Configuración
    ];

    const backupData = {
        meta: {
            exportedAt: new Date().toISOString(),
            version: '1.0'
        },
        data: {}
    };

    try {
        // Ejecutar lecturas en paralelo
        const promises = collectionsToBackup.map(async (colName) => {
            const querySnapshot = await getDocs(collection(db, colName));
            const items = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            return { [colName]: items };
        });

        const results = await Promise.all(promises);
        
        // Combinar resultados
        results.forEach(res => {
            Object.assign(backupData.data, res);
        });

        return backupData;
    } catch (error) {
        console.error("Error creating backup:", error);
        throw new Error("Falló la exportación de datos. Revisa la consola.");
    }
};

/**
 * NUEVO: Restaura la base de datos completa desde un objeto de respaldo JSON.
 * Utiliza batch writes para eficiencia y atomicidad por bloques.
 * ADVERTENCIA: Sobrescribe los documentos existentes que coincidan con los IDs del respaldo.
 */
export const restoreSystemFromBackup = async (backupData) => {
    // 1. Validaciones básicas de estructura
    if (!backupData || !backupData.meta || !backupData.data) {
        throw new Error("El archivo no tiene un formato de respaldo válido (BantCore V1).");
    }

    // 2. Procesamiento por lotes (Firebase limita los batchs a 500 operaciones)
    const BATCH_SIZE = 450; // Margen de seguridad
    let batch = writeBatch(db);
    let operationCounter = 0;
    let totalRestored = 0;

    try {
        // Recorrer cada colección en el backup (ej: 'clients', 'products')
        for (const [collectionName, items] of Object.entries(backupData.data)) {
            
            // Recorrer cada documento en la colección
            for (const item of items) {
                if (!item.id) continue; // Saltar si no tiene ID (integridad)

                const docRef = doc(db, collectionName, item.id);
                // Usamos set() para sobrescribir o crear. 
                // Podríamos usar {merge: true}, pero en una restauración solemos querer 
                // que el estado sea exacto al del respaldo.
                batch.set(docRef, item); 
                
                operationCounter++;
                totalRestored++;

                // Si llenamos el batch, lo enviamos y empezamos uno nuevo
                if (operationCounter >= BATCH_SIZE) {
                    await batch.commit();
                    batch = writeBatch(db); // Nuevo batch limpio
                    operationCounter = 0;
                }
            }
        }

        // 3. Enviar el último lote si quedaron operaciones pendientes
        if (operationCounter > 0) {
            await batch.commit();
        }

        return totalRestored;
    } catch (error) {
        console.error("Error restoring backup:", error);
        throw error;
    }
};